import React, { useState } from "react";
import { IAppState } from "redux/reducers";
import { formUtils, IFormField, useField } from "../../../../../utils/form-utils";
import DetailsPanel from "../../../../shared-ui/details-panel/details-panel";
import { connect } from "react-redux";
import { AuthenticationMode } from "api/entities/account";
import { FormPanel } from "components/shared-ui/form/form-panel/form-panel";
import { useApi } from "utils/hooks/use-api";
import { validators } from "utils/validators";
import { accountService } from "api/services/account-service";
import { PasswordInput } from "components/shared-ui/input/password-input/password-input";
import { FormField } from "components/shared-ui/form/form-field/form-field";
import { PasswordValidationRules } from "components/account-actions/password-rules/password-validation-rules";
import styles from "./change-set-password-panel.module.scss";

export interface IChangeSetPasswordForm {
    currentPassword: IFormField<string>;
    newPassword: IFormField<string>;
    newPasswordConfirmation: IFormField<string>;
}

export interface IChangeSetPasswordFormValue {
    currentPassword: string;
    newPassword: string;
    newPasswordConfirmation: string;
}

const changeSetPasswordFormDefaultValue: IChangeSetPasswordFormValue = {
    currentPassword: "",
    newPassword: "",
    newPasswordConfirmation: ""
}

const mapStateToProps = (state: IAppState) => ({
    user: state.users.currentUser,
});

export interface IChangeSetPasswordProps
    extends ReturnType<typeof mapStateToProps> {

    open: boolean;
    onClose: (newAuthenticationMode: AuthenticationMode) => void;
}

export const ChangeSetPasswordPanel = connect(mapStateToProps, null)((props: IChangeSetPasswordProps) => {
    const [api] = useApi();
    const [isPasswordSetMode, setPasswordSetMode] = useState<boolean>(props.user.authenticationMode === AuthenticationMode.SSOOnly);

    let currentPasswordValidators = [];
    let newPasswordValidators = [
        validators.required("Password is required"),
        (value: string) => validators.isPassword(value) ? "" : "Provided password is not valid"
    ];

    if (!isPasswordSetMode) {
        currentPasswordValidators = [
            validators.required("Password is required"),
            comparePasswordUniqueness("newPassword")
        ];
    }

    const form: IChangeSetPasswordForm = {
        currentPassword: useField<string>(changeSetPasswordFormDefaultValue.currentPassword, currentPasswordValidators),
        newPassword: useField<string>(changeSetPasswordFormDefaultValue.newPassword, newPasswordValidators, [{ asyncValidator: checkIsCurrentPasswordValid }]),
        newPasswordConfirmation: useField<string>(changeSetPasswordFormDefaultValue.newPasswordConfirmation,
            [
                validators.required("Password confirmation is required"),
                comparePasswords
            ])
    };
    const formError = formUtils.getFormError(form);

    function comparePasswordUniqueness(otherFieldName: string): (value: string) => string {
        return (value: string) => {
            return form[otherFieldName].value === value ? "New password should not match the current one" : "";
        };
    }

    async function checkIsCurrentPasswordValid(): Promise<string> {
        if (isPasswordSetMode) {
            return "";
        }

        let isCurrentPasswordValid = await form.currentPassword.validate(form.currentPassword.value);
        return isCurrentPasswordValid ? "" : "New password should not match the current one";
    }

    function comparePasswords(value: string): string {
        return form.newPassword.value !== value ? "Passwords do not match" : ""
    }

    function handleUpdateSetPassword() {
        formUtils.validateAll(form).then((isValid) => {
            if (isValid) {
                const formValue = formUtils.getFormValue<IChangeSetPasswordFormValue>(form);
                let request = {
                    newPassword: formValue.newPassword,
                    newPasswordConfirmation: formValue.newPasswordConfirmation,
                };

                let authenticationMode = props.user.authenticationMode;

                if (isPasswordSetMode) {
                    api(accountService.setPassword(request))
                        .then(() => {
                            formUtils.setFormValue(form, changeSetPasswordFormDefaultValue);
                            setPasswordSetMode(false);
                            if (authenticationMode === AuthenticationMode.SSOOnly) {
                                authenticationMode = AuthenticationMode.SSOAndLocal;
                            }

                            props.onClose(authenticationMode);
                        });
                }
                else {
                    api(accountService.updatePassword(Object.assign(request, {
                        currentPassword: formValue.currentPassword
                    })))
                        .then(() => {
                            formUtils.setFormValue(form, changeSetPasswordFormDefaultValue);
                            props.onClose(authenticationMode);
                        });
                }
            }
        });
    }

    return (
        <DetailsPanel
            open={props.open}
            onCloseClick={() => props.onClose(props.user.authenticationMode)}
            title={isPasswordSetMode ? "Set Password" : "Change Password"}
            className={styles.panel}
        >
            <FormPanel
                className={styles.root}
                okButtonText={isPasswordSetMode ? "Set Password" : "Change Password"}
                okButtonClick={handleUpdateSetPassword}
                errorMessage={formError}
            >
                {!isPasswordSetMode
                    ? <FormField label="Password" testId="current-password-field">
                        <PasswordInput
                            className={styles.password}
                            testId="current-password-input"
                            field={form.currentPassword}
                            placeholder="Current Password"
                        />
                    </FormField>
                    : <></>
                }

                <FormField label="New Password" testId="password-field">
                    <PasswordInput
                        className={styles.password}
                        testId="new-password-input"
                        field={form.newPassword}
                        placeholder="New Password"
                    />
                </FormField>

                <FormField label="Password Confirmation" testId="password-confirmation-field">
                    <PasswordInput
                        className={styles.passwordConfirmation}
                        testId="password-confirmation-input"
                        field={form.newPasswordConfirmation}
                        placeholder="Password Confirmation"
                    />
                </FormField>

                <PasswordValidationRules />
            </FormPanel>
        </DetailsPanel>
    );
});